import { theme } from '@contrast-security-inc/design-system-foundations'
import { SingleSelect } from '@contrast-security-inc/design-system-inputs'
import { css } from '@emotion/css'
import React, { useMemo, useState } from 'react'
import { FormattedMessage, IntlShape } from 'react-intl'
import { theme as deprecatedTheme } from '../../../../styled'
import { ContrastFormattedMessage } from '../../../../components/contrast-formatted-message'
import {
  Filters,
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../../../legacy-design-system/filter'
import { ClearFilterContent } from '../../../../legacy-design-system/grid/column-header'
import { I18nConsumer } from '../../../../components/i18n-provider'
import {
  CsFilterIcon,
  CsHideIcon,
  CsShowIcon
} from '../../../../legacy-design-system/icons'
import {
  QuickFilter,
  QuickFilterDropdown
} from '../../../../legacy-design-system/quick-filter'
import { SearchTextInput } from '../../../../legacy-design-system/search-text'
import { formLabelCss } from '../../../../legacy-design-system/text'
import { WithTooltip } from '../../../../design-system'
import {
  DependencyType,
  ModuleApplication,
  ModulesResponse,
  Report,
  SelectedFilters,
  TreeType
} from './interfaces'

interface Props {
  setCollapsedExpanded: () => void
  allCollapsed: boolean
  setVulnerableStatus: (vulnerableStatus: boolean) => void
  filterTree: (e: string) => void
  totalCount: number
  vulnerableCount: number
  report: Report
  reports: Report[]
  handleReportSelection: (report) => void
  cliVersion: string
  setLibraryFilter: React.Dispatch<React.SetStateAction<DependencyType>>
  setSelectedApplicationId: React.Dispatch<React.SetStateAction<string>>
  selectedFilters: SelectedFilters
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>
  selectedApplicationId: string
  mergedApplications?: ModulesResponse
  treeType: TreeType
}

export const formatDate = (intl: IntlShape, item?: Report): string =>
  intl.formatDate(item && item.snapshotCreatedAt, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour12: true,
    hour: '2-digit',
    minute: '2-digit'
  })

export const FilterTree = ({
  setVulnerableStatus,
  setCollapsedExpanded,
  allCollapsed,
  totalCount,
  vulnerableCount,
  filterTree,
  report,
  reports,
  handleReportSelection,
  cliVersion,
  setLibraryFilter,
  selectedFilters,
  setSelectedFilters,
  setSelectedApplicationId,
  mergedApplications,
  selectedApplicationId,
  treeType
}: Props) => {
  const [selectedDropdownFilter, setSelectedDropdownFilter] = useState('ALL')

  const applications = useMemo(
    () =>
      mergedApplications
        ? [
            mergedApplications.parentApplication,
            ...mergedApplications.applications
          ]
        : [],
    [mergedApplications]
  )

  const initialSelectedItem = useMemo(
    () => applications.find((app) => app.app_id === selectedApplicationId),

    [applications, selectedApplicationId]
  )

  const filterIconStyles = css`
    margin-left: 10px;
    margin-top: -4px;
    ${selectedFilters.dependencies.length > 0
      ? `color: ${theme.color.teal70};`
      : `color: ${theme.color.black};`};
    :hover {
      cursor: pointer;
    }
  `

  const iconNames = {
    CsHideIcon,
    CsShowIcon
  }
  const ExpandCollapseIcon = allCollapsed
    ? iconNames.CsShowIcon
    : iconNames.CsHideIcon

  const onOptionToggle = ({ optionID }: OnOptionToggleProps<string>) => {
    const selectedOptionIndex = selectedFilters.dependencies.indexOf(optionID)

    const updatedArray: string[] = []
    Object.assign(updatedArray, selectedFilters.dependencies)

    if (selectedFilters.dependencies.includes(optionID)) {
      updatedArray.splice(selectedOptionIndex, 1)
    } else {
      updatedArray.push(optionID)
    }

    setSelectedFilters({ dependencies: updatedArray })

    if (
      (updatedArray.includes('Developer') &&
        updatedArray.includes('Production')) ||
      updatedArray.length === 0
    ) {
      setLibraryFilter(DependencyType.ALL)
    } else {
      if (updatedArray.includes('Developer')) {
        setLibraryFilter(DependencyType.DEV)
      }

      if (updatedArray.includes('Production')) {
        setLibraryFilter(DependencyType.PROD)
      }
    }
  }

  const onGroupToggle = ({
    optionsAvailableForSelection
  }: OnGroupToggleProps<string>) => {
    optionsAvailableForSelection.sort()
    selectedFilters.dependencies.sort()
    const availableOptions = optionsAvailableForSelection.join(',')

    const allOptionsSelected =
      availableOptions === selectedFilters.dependencies.join(',')

    if (allOptionsSelected) {
      setSelectedFilters({ dependencies: [] })

      setLibraryFilter(DependencyType.PROD)
    } else {
      setSelectedFilters({
        dependencies: optionsAvailableForSelection
      })

      setLibraryFilter(DependencyType.ALL)
    }
  }

  return (
    <div
      className={css`
        height: 70px;
        margin-bottom: 5px;
      `}
    >
      <div
        className={css`
          margin-bottom: 5px;
          margin-left: 15px;
          display: flex;
        `}
      >
        <span data-testid="QuickFilterDropdown">
          <QuickFilterDropdown
            filters={[
              {
                id: 'ALL',
                name: 'All',
                count: totalCount
              },
              {
                id: 'VULNERABLE',
                name: 'Vulnerable',
                count: vulnerableCount
              }
            ]}
            selectedFilter={selectedDropdownFilter}
            onChange={(selectedFilter: string) => {
              setSelectedDropdownFilter(selectedFilter)

              if (selectedFilter === 'VULNERABLE') {
                setVulnerableStatus(true)
              } else {
                setVulnerableStatus(false)
              }
            }}
            filterToGroup={(filter: QuickFilter) => {
              return filter.name
            }}
            isDrilldown
          />
        </span>
        <SearchTextInput
          className={css`
            margin-left: 15px;
            margin-top: 6px;
          `}
          placeholderI18n={{ id: 'placeholder_findLibrary' }}
          onChange={(e) => filterTree(e)}
          data-testid={'search-text-input-test-id'}
        />
      </div>
      <div
        className={css`
          margin-left: 15px;
        `}
      >
        <div
          className={css`
            float: left;
            display: flex;
            align-items: center;
            height: 30px;
          `}
        >
          <ContrastFormattedMessage
            id={'title_filter'}
            children={(columnTitle) => (
              <div className={formLabelCss}>{columnTitle.toUpperCase()}</div>
            )}
          />
          <div id="dependencyTypeFilters">
            <Filters
              groupConfigs={{
                dependencies: {
                  id: 'dependencies',
                  groupName: {
                    id: 'title_dependencies',
                    defaultMessage: 'Dependencies'
                  },
                  optionToID: ({ key }) => key.id,
                  optionToString: ({ key }) => key.id,
                  options: [
                    {
                      key: {
                        id: 'Developer',
                        label: 'Developer'
                      }
                    },
                    {
                      key: {
                        id: 'Production',
                        label: 'Production'
                      }
                    }
                  ]
                }
              }}
              selections={selectedFilters}
              groupIDs={['dependencies']}
              onGroupToggle={onGroupToggle}
              onOptionToggle={onOptionToggle}
              children={() => (
                <>
                  <CsFilterIcon
                    data-testid="package-type-filter-icon"
                    className={filterIconStyles}
                  />
                  {selectedFilters.dependencies.length > 0 && (
                    <ClearFilterContent
                      className={css`
                        display: inline;
                        color: ${theme.color.teal70};
                      `}
                      onClick={() => {
                        setSelectedFilters({ dependencies: [] })
                        setLibraryFilter(DependencyType.ALL)
                      }}
                    />
                  )}
                </>
              )}
            />
          </div>
          <div
            className={css`
              cursor: pointer;
              margin-left: 33px;
              margin-top: -2px;
              &:hover * {
                color: ${theme.color.black};
              }
            `}
            data-testid="expand-all-test-id"
            onClick={() => setCollapsedExpanded()}
          >
            <ExpandCollapseIcon
              className={css`
                margin-top: 1.5px;
              `}
              size={15}
              color={'grey'}
            />
            <span
              className={css`
                font-size: 10px;
                color: grey;
                margin-bottom: 1px;
              `}
            >
              {!allCollapsed ? (
                <FormattedMessage id={'action_expandAll'} />
              ) : (
                <FormattedMessage id={'action_collapseAll'} />
              )}
            </span>
          </div>
        </div>
        <div
          className={css`
            font-family: ${deprecatedTheme.fontFamilies.secondary};
            color: grey;
            font-size: 12px;
            float: right;
            display: flex;
            align-items: center;
            flex-direction: column;
          `}
        >
          {treeType === 'LegacyCLI' && (
            <WithTooltip
              placement={'top'}
              renderTooltipContent={() => (
                <div
                  className={css`
                    max-width: 280px;
                  `}
                >
                  <FormattedMessage
                    id="detail_dependencyTreeSource"
                    children={(message) => {
                      return <div>{message}</div>
                    }}
                  />
                </div>
              )}
              children={() => (
                <FormattedMessage
                  id="title_source"
                  children={(message) => {
                    return (
                      <div>
                        <b>{message}</b>:&nbsp;
                        <FormattedMessage id="title_cli_tool" />
                        <span>{cliVersion}</span>
                      </div>
                    )
                  }}
                />
              )}
            />
          )}

          <div
            className={css`
              display: flex;
              margin-top: 5px;
            `}
          >
            {applications.length > 0 &&
              mergedApplications &&
              mergedApplications.applications.length > 0 && (
                <>
                  <div
                    className={css`
                      display: flex;

                      input {
                        height: auto !important;
                      }
                    `}
                  >
                    <div
                      className={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                      `}
                    >
                      <b
                        className={css`
                          margin-right: 10px;
                        `}
                      >
                        <FormattedMessage id="title_application" /> :
                      </b>
                    </div>

                    <SingleSelect<ModuleApplication>
                      value={initialSelectedItem}
                      items={applications}
                      onChange={(_, application) => {
                        if (application) {
                          setSelectedApplicationId(application.app_id)
                          setVulnerableStatus(false)
                        }
                      }}
                      itemToString={(application) => {
                        return application.name
                      }}
                      itemToID={(application) => application.app_id}
                      style={{
                        backgroundColor: 'white',
                        paddingTop: 0,
                        width: 300
                      }}
                      allowItemTextWrap
                      className={css`
                        button:first-of-type {
                          display: none !important;
                        }
                      `}
                      testId="select-application"
                    />
                  </div>
                </>
              )}

            <div
              className={css`
                display: flex;
                margin-left: 10px;
                justify-content: center;
                align-items: center;

                margin-right: 10px;
                input {
                  height: auto !important;
                }
              `}
            >
              <b
                className={css`
                  margin-right: 5px;
                `}
              >
                <FormattedMessage id="title_lastUpdated" /> :
              </b>

              <I18nConsumer>
                {({ intl }) => {
                  return (
                    <>
                      <SingleSelect<Report>
                        value={report}
                        items={reports}
                        onChange={(_, newReport) => {
                          if (newReport) {
                            handleReportSelection(newReport)
                          }
                        }}
                        itemToString={(item) => formatDate(intl, item)}
                        itemToID={(item) => String(item.reportId)}
                        style={{
                          backgroundColor: 'white',
                          paddingTop: 0,
                          width: 350,
                          fontSize: '14px'
                        }}
                        allowItemTextWrap
                        className={css`
                          button:first-of-type {
                            display: none !important;
                          }
                        `}
                        testId="select-report"
                      />
                    </>
                  )
                }}
              </I18nConsumer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
